import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Contact', path: '/contact' },
];

function BrandMark({ dark = false }: { dark?: boolean }) {
  const iconColor = dark ? '#1a3a3a' : 'white';
  return (
    <svg
      width="40"
      height="40"
      viewBox="217 157 590 685"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Top shape */}
      <path
        fill={iconColor}
        d="M511.964 156.887C525.432 162.801 545.501 173.891 559.283 180.896L655.777 229.874L754.233 279.503C769.802 287.355 791.435 297.508 806.04 306.001L806.065 385.026C806.071 397.275 806.401 410.964 806.119 423.082L613.12 325.301L563.418 300.17C554.979 295.869 545.296 290.565 536.899 286.588L533.188 284.715C526.14 281.101 518.811 277.223 511.686 273.831C491.8 283.063 468.987 295.455 449.231 305.46L329.909 366.139L330.043 532.607C311.514 526.773 288.526 517.607 269.935 510.802L238.642 499.364C233.439 497.472 226.923 495.348 221.99 493.21L221.9 304.752C239.117 295.45 257.677 286.311 275.178 277.34L400.049 213.756L475.012 175.543L499.205 163.242C502.739 161.45 508.421 158.352 511.964 156.887Z"
      />
      {/* Bottom shape */}
      <path
        fill={iconColor}
        d="M693.501 460.959C715.631 468.745 737.714 476.661 759.75 484.709C773.034 489.526 788.446 494.715 801.429 499.921L801.371 612.146C801.344 638.491 800.903 666.121 801.482 692.359C776.663 703.935 749.57 718.473 725.101 731.017L585.147 802.608C564.985 812.907 544.252 824.226 524.018 834.089C519.964 836.044 515.537 838.476 511.506 840.573L363.402 765.582C360.916 764.612 356.228 762.021 353.68 760.699L259.737 713.264C246.988 706.807 229.722 698.637 217.541 691.677C217.407 651.996 216.97 611.156 217.466 571.542C227.568 576.003 238.887 582.019 248.922 587.044L301.161 613.139L511.638 719.928L619.341 665.079C643.117 653.011 669.415 639.03 693.356 627.887C692.61 598.525 693.155 567.516 693.131 537.988C693.11 513.269 692.505 485.352 693.501 460.959Z"
      />
      {/* Center rectangle */}
      <path
        fill={iconColor}
        d="M396.593 387.243C403.371 386.3 426.476 386.922 434.731 386.911L518.898 386.879L591.816 386.894C597.71 386.895 621.346 386.612 625.635 387.414L626.312 387.594C627.379 389.02 626.927 451.642 626.929 457.841L626.966 605.499L396.538 605.501L396.593 387.243Z"
      />
    </svg>
  );
}

function HexLogo({ dark = false }: { dark?: boolean }) {
  return (
    <Link to="/" className="flex items-center gap-3 cursor-pointer">
      <div className="w-10 h-10 flex items-center justify-center shrink-0">
        <BrandMark dark={dark} />
      </div>
      {/* Wordmark */}
      <div className="flex flex-col leading-none">
        <span
          className={`text-lg font-extrabold tracking-tight transition-colors duration-300 ${dark ? 'text-[#1a3a3a]' : 'text-white'}`}
          style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '0.05em' }}
        >
          CONTINUOUS
        </span>
        <span
          className="text-[10px] font-bold tracking-[0.35em] text-[#c8a84b]"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          STAFFING
        </span>
      </div>
    </Link>
  );
}

export { HexLogo };

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const isHome = location.pathname === '/';

  const useDarkStyle = scrolled || !isHome;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        useDarkStyle ? 'bg-white border-b border-gray-100' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <HexLogo dark={useDarkStyle} />

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 whitespace-nowrap ${
                location.pathname === link.path
                  ? 'text-[#c8a84b]'
                  : useDarkStyle
                  ? 'text-[#1a3a3a] hover:text-[#c8a84b]'
                  : 'text-white/90 hover:text-white'
              }`}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className={`ml-2 px-5 py-2.5 rounded-md text-sm font-bold tracking-wide transition-all duration-200 whitespace-nowrap cursor-pointer ${
              useDarkStyle
                ? 'bg-[#1a3a3a] text-white hover:bg-[#0d2b2b]'
                : 'bg-[#c8a84b] text-white hover:bg-[#b8943b]'
            }`}
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Get in Touch
          </Link>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 cursor-pointer p-1"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={`block w-6 h-0.5 transition-colors duration-300 ${useDarkStyle ? 'bg-[#1a3a3a]' : 'bg-white'}`}
            />
          ))}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-5 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-semibold tracking-wide ${
                location.pathname === link.path ? 'text-[#c8a84b]' : 'text-[#1a3a3a]'
              }`}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="px-5 py-2.5 rounded-md bg-[#1a3a3a] text-white text-sm font-bold text-center whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Get in Touch
          </Link>
        </div>
      )}
    </header>
  );
}
